// Realtime data sync for FlowchartBuilder — the incoming-update handler and the
// outgoing autosave/merge effect, extracted out of the ~3900-line component body.
//
// This hook owns the sync refs (lastSyncedContentRef, flowchartVersionRef, etc.)
// and returns them, because ~20 OTHER places in FlowchartBuilder.tsx (loading a
// flowchart, restoring a version, the manual "save now" flow, changelog diffing)
// also read/write them directly. Returning the same ref objects instead of copying
// their values means those call sites keep mutating `.current` on the exact
// objects this hook's own effects read from — no behavior change versus when the
// refs were declared directly in FlowchartBuilder.tsx, just a different
// declaration site. Do not change any of these refs to plain state — the whole
// point of a ref here is that writing `.current` does not trigger a re-render.
import { useEffect, useRef } from 'react';
import { useRealtimeSync } from './useRealtimeSync';
import { mergeById, mergeByKey, mergeScalar, guardWholesaleWipeout, SUSPICIOUS_WIPEOUT_THRESHOLD } from './lib/collabMerge';
import { evaluateRealtimeUpdate, writeWithConflictMerge } from './lib/collabSync';
import { logger } from './lib/logger';
import supabase from './supabase';
import type { Box, Arrow, Department, Area, Resource, DurationUnit } from './types/index';

// Shape shared by the "local content" snapshot, the parsed sync baseline, and a
// freshly-refetched row, so the conflict merge (writeWithConflictMerge below) can
// treat all three uniformly.
export interface FlowchartContent {
  title: string;
  boxes: Box[];
  arrows: Arrow[];
  departments: Department[];
  areas: Area[];
  resources: Resource[];
}

interface FreshFlowchartRow {
  title: string | null;
  boxes: unknown;
  arrows: unknown;
  departments: unknown;
  areas: unknown;
  resources: unknown;
  version: number;
}

// Shape of a `flowcharts` row as delivered by Supabase Realtime's
// postgres_changes payload (payload.new, spread — see useRealtimeSync.js). The
// jsonb columns arrive already-parsed as an array in the common case, but the
// Realtime client doesn't guarantee that for every payload size/version, so
// onUpdate below explicitly narrows each with Array.isArray/typeof checks rather
// than trusting a single assumed shape — `unknown` here reflects that honestly
// instead of a specific type that would just paper over the same ambiguity.
interface FlowchartRealtimeRow {
  id: string;
  title: string | null;
  boxes: unknown;
  arrows: unknown;
  departments: unknown;
  areas: unknown;
  resources: unknown;
  version?: number;
  updated_at?: string;
  updated_by_email?: string | null;
  gantt_start_date?: string | null;
}

interface UseFlowchartSyncParams {
  currentId: string | null;
  userId: string | undefined;
  userEmail: string | undefined;
  onlineUsersCount: number;
  syncRetryTick: number;

  boxes: Box[];
  arrows: Arrow[];
  title: string;
  departments: Department[];
  areas: Area[];
  resources: Resource[];
  durationUnit: DurationUnit;
  // Must be forwarded into every syncToDatabase call below — useRealtimeSync.js's
  // buildPayload writes gantt_start_date: null whenever its own ganttStartDate
  // input is falsy/omitted, so leaving this out of a write silently resets the
  // column to null on every single autosave/manual save, undoing a one-time
  // backfill (or the create-time default) the very next time anything here saves.
  ganttStartDate: Date;

  setBoxes: (b: Box[]) => void;
  setArrows: (a: Arrow[]) => void;
  setTitle: (t: string) => void;
  setDepartments: (d: Department[]) => void;
  setAreas: (a: Area[]) => void;
  setResources: (r: Resource[]) => void;
  setNextId: (n: number) => void;
  setGanttStartDate: (d: Date) => void;
  setLastWriteToast: (v: string | null) => void;
  setSyncFailed: (v: boolean) => void;
  setSyncRetryTick: React.Dispatch<React.SetStateAction<number>>;

  normalizeArrows: (rawArrows: unknown[], boxes: Box[]) => Arrow[];
  normalizeResources: (raw: unknown) => Resource[];
  diffBoxChanges: (prev: Box[], curr: Box[], prevArrows: Arrow[], currArrows: Arrow[]) => string | null;
  logChange: (flowchartId: string, action: string) => void;
}

export function useFlowchartSync(params: UseFlowchartSyncParams) {
  const {
    currentId, userId, userEmail, onlineUsersCount, syncRetryTick,
    boxes, arrows, title, departments, areas, resources, durationUnit, ganttStartDate,
    setBoxes, setArrows, setTitle, setDepartments, setAreas, setResources,
    setNextId, setGanttStartDate, setLastWriteToast, setSyncFailed, setSyncRetryTick,
    normalizeArrows, normalizeResources, diffBoxChanges, logChange,
  } = params;

  // Sync refs — owned here, returned below so FlowchartBuilder.tsx's other ~20
  // call sites (load, restore version, manual save, changelog diffing) can still
  // read/write the exact same ref objects. Initial values match what these refs
  // had when they were declared directly in FlowchartBuilder.tsx.
  // Always-current mirror of currentId, read from inside the debounced autosave
  // callback below. That callback's OWN `currentId` binding is a plain closure
  // variable captured at the moment its setTimeout was scheduled — clearTimeout on
  // effect cleanup cancels a TIMER that hasn't fired yet, but does nothing once the
  // callback has already started running and is mid-`await` (syncToDatabase, or the
  // conflict-resolution fetch), which given normal network latency is an easily
  // reachable window if the user switches to a different flowchart in the
  // meantime. Comparing this ref (always current) against that stale closure
  // value right after each await is what lets the callback detect "I'm no longer
  // looking at this flowchart" and bail out — otherwise it would call setBoxes/
  // setArrows/etc with the OLD flowchart's (merged) data, corrupting whatever is
  // now on screen for the NEWLY opened one.
  const currentIdRef = useRef(currentId);
  useEffect(() => { currentIdRef.current = currentId; }, [currentId]);

  const lastRemoteContentRef = useRef<string | null>(null); // last received remote content (for change detection)
  const lastSyncedContentRef = useRef<string | null>(null); // last content sent to the DB (avoids unnecessary saves)
  const lastLocalSaveTimeRef = useRef<number>(0);           // timestamp of the last local save (ms since epoch)
  const flowchartVersionRef = useRef<number>(0);
  const syncRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAutoLogRef = useRef<number>(0); // timestamp of the last autosave changelog entry
  const lastLoggedBoxesRef = useRef<Box[]>([]); // snapshot of boxes at the last changelog entry
  const lastLoggedArrowsRef = useRef<Arrow[]>([]); // snapshot of arrows at the last changelog entry
  // Guards against overlapping autosave cycles applying their result out of
  // order. Each debounce firing snapshots boxes/title/etc via closure, then
  // does 1-2 network round trips (write, and on conflict also fetch+merge)
  // before calling setBoxes/setTitle/etc with the result. If the user keeps
  // editing, a NEWER cycle can start (and finish, with a clean fast write)
  // while an OLDER cycle is still stuck in its slower conflict round trip —
  // and when that older cycle finally resolves, it would otherwise overwrite
  // the newer cycle's already-applied, more complete state with its own
  // stale snapshot, silently discarding whatever was edited in between. More
  // concurrent collaborators means more version conflicts, which is why this
  // was reported as "my edit got undone" specifically when another user was
  // also active. Claimed right before the async write starts; checked right
  // after it resolves — a mismatch means a newer cycle has since started, so
  // this stale result must not be applied.
  const autosaveGenerationRef = useRef(0);

  useEffect(() => () => { if (syncRetryTimerRef.current) clearTimeout(syncRetryTimerRef.current); }, []);

  // REALTIME SYNC - Live collaboration between users
  const { syncToDatabase, registerOwnWrite } = useRealtimeSync(
    currentId,  // Current flowchart ID
    userId,     // Logged-in user ID
    {
      enabled: !!currentId,
      onUpdate: (updatedData: FlowchartRealtimeRow) => {
        const rawNewBoxes: any[] = Array.isArray(updatedData.boxes) ? updatedData.boxes
          : typeof updatedData.boxes === 'string' ? JSON.parse(updatedData.boxes || '[]') : [];
        const newBoxes: Box[] = rawNewBoxes.map(b => (!b.text && b.title) ? { ...b, text: b.title } : b);
        const newArrows = normalizeArrows(
          Array.isArray(updatedData.arrows) ? updatedData.arrows
            : typeof updatedData.arrows === 'string' ? JSON.parse(updatedData.arrows || '[]') : [],
          newBoxes
        );
        const newDepartments = Array.isArray(updatedData.departments) ? updatedData.departments
          : typeof updatedData.departments === 'string' ? JSON.parse(updatedData.departments || '[]') : [];
        const newAreas = Array.isArray(updatedData.areas) ? updatedData.areas
          : typeof updatedData.areas === 'string' ? JSON.parse(updatedData.areas || '[]') : [];
        const newResources: Resource[] = normalizeResources(updatedData.resources);
        const newTitle = updatedData.title || '';

        // Refuse updates that would empty the canvas while there's data present
        // (protects against stale realtime events from another, empty session)
        if (newBoxes.length === 0 && boxes.length > 0) {
          logger.warn('⚠️ Realtime update genegeerd: zou', boxes.length, 'boxes wissen met lege data');
          return;
        }

        const incomingContent = JSON.stringify({ boxes: newBoxes, arrows: newArrows, departments: newDepartments, title: newTitle, areas: newAreas, resources: newResources });
        const currentLocalContent = JSON.stringify({ boxes, arrows, departments, title, areas, resources });

        // lastSyncedContentRef is stored in the autosave effect's own shape
        // (durationUnit included) — re-key it to match incomingContent's shape before
        // comparing, so the echo-guard inside evaluateRealtimeUpdate isn't fooled by
        // unrelated fields that were never part of this realtime payload.
        let lastSyncedComparable: string | null = null;
        if (lastSyncedContentRef.current) {
          try {
            const synced = JSON.parse(lastSyncedContentRef.current);
            lastSyncedComparable = JSON.stringify({ boxes: synced.boxes, arrows: synced.arrows, departments: synced.departments, title: synced.title, areas: synced.areas, resources: synced.resources });
          } catch (_) {}
        }

        const decision = evaluateRealtimeUpdate({
          incomingId: updatedData.id,
          currentId: currentId ?? '',
          incomingContent,
          lastRemoteContent: lastRemoteContentRef.current,
          lastSyncedContent: lastSyncedComparable,
          currentLocalContent,
          updatedAt: updatedData.updated_at,
          lastLocalSaveTime: lastLocalSaveTimeRef.current,
          // All 4 content values above are already JSON.stringify'd. Without this,
          // the default equals JSON.stringify's them AGAIN (re-escaping an already-
          // serialized string) on every dedup/echo/metadata-only comparison — pure
          // waste that scales with document size and runs on every single realtime
          // message, competing for main-thread time with an active drag on a large,
          // frequently-collaborated flowchart.
          equals: (a, b) => a === b,
        });
        if (decision === 'ignore') return;

        lastRemoteContentRef.current = incomingContent;
        // Adopt the version of the accepted remote state, so our next autosave
        // builds on top of it instead of causing a conflict.
        if (typeof updatedData.version === 'number') flowchartVersionRef.current = updatedData.version;

        // Metadata-only broadcast (a gantt setting or view option changed via
        // updateFlowchartMeta): boxes/arrows/title/departments/areas/resources are
        // UNCHANGED even though updated_at/version bumped, so skip the O(n)
        // mergeById/JSON.stringify work below entirely, and skip the "changes made
        // by" toast — updated_by_email here still names whoever made the last REAL
        // content edit, unrelated to this update.
        if (decision === 'metadata-only') {
          if (updatedData.gantt_start_date) {
            const d = new Date(updatedData.gantt_start_date);
            if (!isNaN(d.getTime())) setGanttStartDate(d);
          }
          return;
        }

        const resolvedDepts = newDepartments.length > 0 ? newDepartments : [
          { name: 'Category A', color: '#dcfce7' },
          { name: 'Category B', color: '#fce7f3' },
          { name: 'Category C', color: '#dbeafe' }
        ];

        // Merge with local UNSAVED changes instead of blindly overwriting them.
        // The autosave debounce is 2-10s, so there's a real window where a remote
        // update — even a completely unrelated write from an otherwise-idle other
        // user — used to arrive while this client had in-progress local edits (e.g.
        // someone mid-typing a note title) and setBoxes(newBoxes) would replace the
        // whole array, silently discarding those edits. Same per-item "locally
        // changed since base wins" rule as the conflict-resolution branch below,
        // with `base` = the last content we know is actually in the DB.
        const base = (() => { try { return lastSyncedContentRef.current ? JSON.parse(lastSyncedContentRef.current) : null; } catch { return null; } })();
        // Fast path: if local content is byte-for-byte identical to the last-synced
        // baseline, there are no in-progress local edits to protect, so the per-item
        // mergeById/mergeByKey below — which JSON.stringifies every single box, arrow,
        // department, area and resource to detect divergence — would just reproduce
        // newX/resolvedDepts anyway (nothing local to prefer over remote). Skipping it
        // in this, the common, edits-are-elsewhere case avoids real synchronous
        // main-thread cost on a large, actively-collaborated flowchart: with two other
        // users saving every few seconds this used to run on every one of their saves,
        // which is exactly what was stalling an in-progress pan on a large map like
        // "DR1 DMLT" (never visible solo, since there's no realtime traffic to react to).
        const noLocalDivergence = base !== null && lastSyncedComparable !== null && currentLocalContent === lastSyncedComparable;
        const skipMerge = !base || noLocalDivergence;
        // skipMerge means "no local divergence" — boxes/arrows/etc. here still
        // equal base, so they're exactly what guardWholesaleWipeout needs as the
        // safe fallback. Without this, a corrupted remote row (a large list gone
        // to `[]`) would get adopted with zero protection, even though the full
        // merge branch right below is now guarded against the same thing.
        const mergedBoxes = skipMerge ? guardWholesaleWipeout(boxes, newBoxes) : mergeById<Box>(base.boxes ?? [], boxes, newBoxes);
        const mergedBoxIds = new Set(mergedBoxes.map(b => b.id));
        const mergedArrows = skipMerge
          ? guardWholesaleWipeout(arrows, newArrows)
          : mergeById<Arrow>(base.arrows ?? [], arrows, newArrows).filter(a => mergedBoxIds.has(a.fromBox) && mergedBoxIds.has(a.toBox));
        const mergedTitle = skipMerge ? newTitle : mergeScalar(base.title ?? '', title, newTitle);
        // Per-item merge (not mergeScalar) for these three: mergeScalar treats the
        // whole list as one opaque value and picks local-or-remote wholesale,
        // which silently drops the OTHER side's additions whenever the list
        // itself changed — this is exactly how a flowchart's resources list was
        // lost in production (one collaborator's stale local list clobbered
        // another's newly-added resources). See lib/collabMerge.ts's doc comment.
        const mergedDepts = skipMerge ? guardWholesaleWipeout(departments, resolvedDepts) : mergeByKey<Department>(base.departments ?? [], departments, resolvedDepts, d => d.name);
        const mergedAreas = skipMerge ? guardWholesaleWipeout(areas, newAreas) : mergeById<Area>(base.areas ?? [], areas, newAreas);
        // Prefer the client-generated id when present (a resource just created but not
        // yet named — two users clicking "Add Resource" around the same time would
        // otherwise both produce a { name: '' } entry and collide on the same key,
        // silently dropping one). Legacy resources without an id still key by name.
        const mergedResources = skipMerge ? guardWholesaleWipeout(resources, newResources) : mergeByKey<Resource>(base.resources ?? [], resources, newResources, r => r.id ?? r.name);

        // Only mark this as "already synced" when nothing local actually survived the
        // merge (the common case: no in-progress local edits, so mergedX === newX for
        // everything) — that's what lets the autosave effect skip the pointless
        // round-trip of writing the same remote content straight back (see the
        // shape-matching note below), and avoids a false "inactive user made changes"
        // toast. When local edits DID survive, lastSyncedContentRef must stay at its
        // old value so the next autosave tick sees the merged result as new content
        // and actually persists it — otherwise that edit (and the remote's other
        // changes) would never make it to the DB at all.
        const mergedMatchesRemote =
          JSON.stringify(mergedBoxes) === JSON.stringify(newBoxes) &&
          JSON.stringify(mergedArrows) === JSON.stringify(newArrows) &&
          mergedTitle === newTitle &&
          JSON.stringify(mergedDepts) === JSON.stringify(resolvedDepts) &&
          JSON.stringify(mergedAreas) === JSON.stringify(newAreas) &&
          JSON.stringify(mergedResources) === JSON.stringify(newResources);
        if (mergedMatchesRemote) {
          // Shape must match exactly what the autosave effect's currentContent uses
          // (durationUnit, not endTimeDisplay — that's a per-user setting now, see
          // useUserFlowchartViewSettings.ts, no longer part of this fingerprint) —
          // otherwise the two JSON strings can never be equal even when nothing local
          // actually changed, and every client (including pure viewers) fires a
          // needless write-back right after receiving someone else's update, bumping
          // `version` and risking a spurious conflict-merge for whoever is editing.
          lastSyncedContentRef.current = JSON.stringify({
            title: mergedTitle, boxes: mergedBoxes, arrows: mergedArrows,
            departments: mergedDepts, areas: mergedAreas, durationUnit, resources: mergedResources
          });
        }

        setBoxes(mergedBoxes);
        setArrows(mergedArrows);
        setTitle(mergedTitle);
        setDepartments(mergedDepts);
        setAreas(mergedAreas);
        setResources(mergedResources);

        // Calculate nextId based on highest ID to prevent duplicates
        const maxBoxId = mergedBoxes.length > 0 ? Math.max(...mergedBoxes.map(b => b.id)) : 0;
        const maxArrowId = mergedArrows.length > 0 ? Math.max(...mergedArrows.map(a => a.id)) : 0;
        setNextId(Math.max(maxBoxId, maxArrowId) + 1);

        // Sync gantt_start_date if another user changed it
        if (updatedData.gantt_start_date) {
          const d = new Date(updatedData.gantt_start_date);
          if (!isNaN(d.getTime())) setGanttStartDate(d);
        }

        // Only show a notification if another user made the change
        if (updatedData.updated_by_email && updatedData.updated_by_email !== userEmail) {
          setLastWriteToast(updatedData.updated_by_email);
          setTimeout(() => setLastWriteToast(null), 4000);
        }
      },
      onError: (error: any) => {
        logger.error('❌ Realtime sync error:', error);
      }
    }
  );

  const parseCol = (v: unknown): any[] => Array.isArray(v) ? v : (typeof v === 'string' ? (() => { try { return JSON.parse(v || '[]'); } catch { return []; } })() : []);

  // One optimistic-locked write attempt for `content` against `base`, shared by
  // the debounced autosave effect below and saveNow() (the manual "Save"
  // button/Ctrl+S) — both need EXACTLY the same conflict-merge semantics, and
  // having two independent copies is exactly how a flowchart's `resources` list
  // was previously lost to a bad merge (see lib/collabMerge.ts's doc comment).
  const attemptWrite = (content: FlowchartContent, base: FlowchartContent | null) =>
    writeWithConflictMerge<FlowchartContent, FreshFlowchartRow>({
      write: async () => {
        const result = await syncToDatabase({
          title: content.title, boxes: content.boxes, arrows: content.arrows,
          departments: content.departments, areas: content.areas, resources: content.resources,
          durationUnit, ganttStartDate, updatedByEmail: userEmail,
        }, flowchartVersionRef.current);
        if (result?.conflict) return { data: null, error: null };
        if (!result?.success) return { data: null, error: result?.error ?? new Error('Sync failed') };
        return { data: { updated_at: result.updatedAt, version: result.version }, error: null };
      },
      expectedVersion: flowchartVersionRef.current,
      fetchFresh: async () => {
        const { data, error } = await supabase
          .from('flowcharts')
          .select('title, boxes, arrows, departments, areas, resources, version')
          .eq('id', currentId)
          .single();
        return { data, error };
      },
      parseFresh: (row) => {
        const freshBoxes: Box[] = parseCol(row.boxes);
        return {
          title: row.title || '',
          boxes: freshBoxes,
          arrows: normalizeArrows(parseCol(row.arrows), freshBoxes),
          departments: parseCol(row.departments),
          areas: parseCol(row.areas),
          resources: normalizeResources(row.resources),
        };
      },
      // A fetched remote row with boxes but NO arrows at all, where we locally
      // have more than a handful, is just as suspicious as an empty remote
      // altogether — same reasoning as collabMerge.ts's own wipeout guard,
      // applied here so an already-corrupted remote row never even enters the
      // merge in the first place.
      isEmpty: (c) => c.boxes.length === 0 || (c.arrows.length === 0 && content.arrows.length > SUSPICIOUS_WIPEOUT_THRESHOLD),
      hasLocalContent: content.boxes.length > 0,
      base,
      local: content,
      merge: (b, l, r) => {
        const mergedBoxes = mergeById<Box>(b?.boxes ?? [], l.boxes, r.boxes);
        const boxIds = new Set(mergedBoxes.map(box => box.id));
        return {
          title: mergeScalar(b?.title ?? l.title, l.title, r.title),
          boxes: mergedBoxes,
          arrows: mergeById<Arrow>(b?.arrows ?? [], l.arrows, r.arrows).filter(a => boxIds.has(a.fromBox) && boxIds.has(a.toBox)),
          // Per-item merge, not mergeScalar — see the comment above onUpdate's
          // equivalent merge, and lib/collabMerge.ts's doc comment.
          departments: mergeByKey<Department>(b?.departments ?? l.departments, l.departments, r.departments, d => d.name),
          areas: mergeById<Area>(b?.areas ?? l.areas, l.areas, r.areas),
          resources: mergeByKey<Resource>(b?.resources ?? l.resources, l.resources, r.resources, res => res.id ?? res.name),
        };
      },
    });

  // Manual "Save" (button / Ctrl+S). Unlike the debounced autosave below, this
  // must actually persist right away and report success/failure — so unlike
  // that effect, a 'merged' outcome here retries the write once immediately
  // with the merged content, instead of leaving it for the next autosave tick.
  //
  // This replaces what used to be an unconditional, version-check-free write
  // (the old syncImmediately) — every manual save silently overwrote the ENTIRE
  // row with whatever this client currently had, discarding any changes another
  // user had made since this client's last sync. That is likely how a
  // just-added connector could vanish: anyone clicking Save (or Ctrl+S) with a
  // local state that predates someone else's edit would blindly wipe it out,
  // regardless of how well-protected the autosave/realtime paths were.
  async function saveNow(): Promise<{ success: boolean; content: FlowchartContent }> {
    if (!currentId) return { success: false, content: { title, boxes, arrows, departments, areas, resources } };
    const base: FlowchartContent | null = (() => { try { return lastSyncedContentRef.current ? JSON.parse(lastSyncedContentRef.current) : null; } catch { return null; } })();
    // Guard the WRITE itself, not just the merge-on-conflict path: a clean write
    // (no version conflict) persists whatever this client's own React state
    // currently holds with zero validation — confirmed in production as how a
    // flowchart's `resources` went from 37 to [] with no merge involved at all
    // (this client's own local `resources` was already wrongly empty at save
    // time; boxes/arrows happened to still be correct, so only resources was
    // lost). If a list collapsed to empty relative to the last-known-good
    // baseline, don't trust it — same reasoning/threshold as
    // guardWholesaleWipeout's other use, just applied before the write instead
    // of only after a conflict.
    const localContent: FlowchartContent = base ? {
      title,
      boxes: guardWholesaleWipeout(base.boxes, boxes),
      arrows: guardWholesaleWipeout(base.arrows, arrows),
      departments: guardWholesaleWipeout(base.departments, departments),
      areas: guardWholesaleWipeout(base.areas, areas),
      resources: guardWholesaleWipeout(base.resources, resources),
    } : { title, boxes, arrows, departments, areas, resources };
    // Self-heal this tab's own display too, not just what gets persisted — the
    // guard only substitutes a field's reference when it actually tripped, so
    // this only re-renders when something was genuinely wrong.
    if (localContent.boxes !== boxes) setBoxes(localContent.boxes);
    if (localContent.arrows !== arrows) setArrows(localContent.arrows);
    if (localContent.departments !== departments) setDepartments(localContent.departments);
    if (localContent.areas !== areas) setAreas(localContent.areas);
    if (localContent.resources !== resources) setResources(localContent.resources);

    const outcome = await attemptWrite(localContent, base);
    let finalContent = localContent;
    let writtenVersion: number | null = null;

    if (outcome.outcome === 'written') {
      writtenVersion = outcome.version;
    } else if (outcome.outcome === 'merged') {
      finalContent = outcome.mergedContent;
      flowchartVersionRef.current = outcome.version;
      const maxBoxId = finalContent.boxes.length > 0 ? Math.max(...finalContent.boxes.map(b => b.id)) : 0;
      const maxArrowId = finalContent.arrows.length > 0 ? Math.max(...finalContent.arrows.map(a => a.id)) : 0;
      setNextId(Math.max(maxBoxId, maxArrowId) + 1);
      setBoxes(finalContent.boxes);
      setArrows(finalContent.arrows);
      setTitle(finalContent.title);
      setDepartments(finalContent.departments.length > 0 ? finalContent.departments : departments);
      setAreas(finalContent.areas);
      setResources(finalContent.resources);
      logger.warn('🔀 Sync conflict opgelost via merge tijdens handmatig opslaan (versie:', outcome.version, ')');
      const retryResult = await syncToDatabase({
        title: finalContent.title, boxes: finalContent.boxes, arrows: finalContent.arrows,
        departments: finalContent.departments, areas: finalContent.areas, resources: finalContent.resources,
        durationUnit, ganttStartDate, updatedByEmail: userEmail,
      }, flowchartVersionRef.current);
      if (retryResult?.success && typeof retryResult.version === 'number') writtenVersion = retryResult.version;
      // If the retry ALSO conflicts (rare), give up for this click — the merged
      // state is already applied locally, so the next autosave tick (or another
      // manual save) picks it up.
    } else if (outcome.outcome === 'empty-remote-protected') {
      flowchartVersionRef.current = outcome.version;
    }

    if (writtenVersion !== null) {
      flowchartVersionRef.current = writtenVersion;
      lastSyncedContentRef.current = JSON.stringify({
        title: finalContent.title, boxes: finalContent.boxes, arrows: finalContent.arrows,
        departments: finalContent.departments, areas: finalContent.areas, durationUnit,
        resources: finalContent.resources,
      });
      lastLocalSaveTimeRef.current = Date.now();
      setSyncFailed(false);
      return { success: true, content: finalContent };
    }
    setSyncFailed(true);
    return { success: false, content: finalContent };
  }

  // REALTIME AUTO-SYNC - Sync changes to database every 2 seconds
  useEffect(() => {
    if (!currentId || (boxes.length === 0 && arrows.length === 0)) return;

    // Debounced sync to database
    const timeoutId = setTimeout(async () => {
      const base: FlowchartContent | null = (() => { try { return lastSyncedContentRef.current ? JSON.parse(lastSyncedContentRef.current) : null; } catch { return null; } })();
      // Guard the WRITE itself (see saveNow's identical comment) — a clean write
      // with no version conflict persists whatever this client's own React
      // state currently holds with zero validation, which is how a flowchart's
      // `resources` went from 37 to [] in production with no merge involved at
      // all. Build both the write payload AND the "did anything change" /
      // lastSyncedContentRef comparison from the SAME guarded values, so a
      // tripped guard is treated as "this field didn't actually change".
      const gBoxes = base ? guardWholesaleWipeout(base.boxes, boxes) : boxes;
      const gArrows = base ? guardWholesaleWipeout(base.arrows, arrows) : arrows;
      const gDepartments = base ? guardWholesaleWipeout(base.departments, departments) : departments;
      const gAreas = base ? guardWholesaleWipeout(base.areas, areas) : areas;
      const gResources = base ? guardWholesaleWipeout(base.resources, resources) : resources;
      if (gBoxes !== boxes) setBoxes(gBoxes);
      if (gArrows !== arrows) setArrows(gArrows);
      if (gDepartments !== departments) setDepartments(gDepartments);
      if (gAreas !== areas) setAreas(gAreas);
      if (gResources !== resources) setResources(gResources);

      const currentContent = JSON.stringify({ title, boxes: gBoxes, arrows: gArrows, departments: gDepartments, areas: gAreas, durationUnit, resources: gResources });
      if (lastSyncedContentRef.current === currentContent) return; // nothing changed, skip

      const localContent: FlowchartContent = { title, boxes: gBoxes, arrows: gArrows, departments: gDepartments, areas: gAreas, resources: gResources };

      const myGeneration = ++autosaveGenerationRef.current;
      const outcome = await attemptWrite(localContent, base);

      // The write/fetch above already landed against whichever flowchart this
      // closure's own `currentId` says it targeted — but if the user has since
      // navigated to a different flowchart, applying the outcome (merge state,
      // marking this content as synced) must NOT run against the state now on
      // screen. See currentIdRef's own comment for why this can't just rely on
      // effect cleanup/clearTimeout.
      if (currentIdRef.current !== currentId) return;
      // A newer autosave cycle has since started (the user kept editing while
      // this one was still in its network round trip) — its own, more current
      // snapshot supersedes this one. See autosaveGenerationRef's own comment.
      if (autosaveGenerationRef.current !== myGeneration) return;

      switch (outcome.outcome) {
        case 'written': {
          flowchartVersionRef.current = outcome.version;
          lastSyncedContentRef.current = currentContent;
          lastLocalSaveTimeRef.current = Date.now(); // track when we last saved locally
          setSyncFailed(false);
          // Log to changelog at most once per 5 minutes (successful saves only)
          const now = Date.now();
          if (currentId && now - lastAutoLogRef.current >= 5 * 60 * 1000) {
            const summary = diffBoxChanges(lastLoggedBoxesRef.current, boxes, lastLoggedArrowsRef.current, arrows);
            // Baseline always advances, whether or not we log below — otherwise a
            // move/resize that happens now would still show up in the NEXT diff too.
            lastLoggedBoxesRef.current = JSON.parse(JSON.stringify(boxes));
            lastLoggedArrowsRef.current = [...arrows];
            // null = nothing but a move/resize (or truly nothing) since the last log —
            // skip silently instead of writing a vague "Minor changes" entry, and don't
            // spend the 5-minute quiet window on a save that produced no log line.
            if (summary) {
              lastAutoLogRef.current = now;
              logChange(currentId, `Auto-saved: ${summary}`);
            }
          }
          return;
        }
        case 'merged': {
          // Leave lastSyncedContentRef untouched — the next autosave tick sees the
          // merged result as new content and actually persists it.
          const { mergedContent } = outcome;
          flowchartVersionRef.current = outcome.version;
          const maxBoxId = mergedContent.boxes.length > 0 ? Math.max(...mergedContent.boxes.map(b => b.id)) : 0;
          const maxArrowId = mergedContent.arrows.length > 0 ? Math.max(...mergedContent.arrows.map(a => a.id)) : 0;
          setNextId(Math.max(maxBoxId, maxArrowId) + 1);
          setBoxes(mergedContent.boxes);
          setArrows(mergedContent.arrows);
          setTitle(mergedContent.title);
          setDepartments(mergedContent.departments.length > 0 ? mergedContent.departments : departments);
          setAreas(mergedContent.areas);
          setResources(mergedContent.resources);
          logger.warn('🔀 Sync conflict opgelost via merge (nieuwe versie:', outcome.version, ')');
          return;
        }
        case 'empty-remote-protected':
          // An empty remote state must never wipe a populated canvas. Only adopt
          // the version, so the next autosave can save the local (populated)
          // state again.
          flowchartVersionRef.current = outcome.version;
          return;
        case 'no-real-conflict':
        case 'error':
          // no-real-conflict: the update hit 0 rows for a reason other than a
          // version conflict (e.g. no write permission). error: the write itself
          // failed, or fetching the fresh state after a conflict failed. Either
          // way: don't mark as synced, show the banner, retry in 5s.
          setSyncFailed(true);
          if (syncRetryTimerRef.current) clearTimeout(syncRetryTimerRef.current);
          syncRetryTimerRef.current = setTimeout(() => setSyncRetryTick(t => t + 1), 5000);
          return;
      }
    }, onlineUsersCount > 0 ? 2000 : 10000); // 2s when collaborating, 10s solo

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxes, arrows, areas, title, departments, resources, durationUnit, currentId, syncToDatabase, userEmail, onlineUsersCount, syncRetryTick]);

  return {
    syncToDatabase, registerOwnWrite, saveNow,
    lastSyncedContentRef, lastRemoteContentRef, lastLocalSaveTimeRef, flowchartVersionRef,
    syncRetryTimerRef, lastAutoLogRef, lastLoggedBoxesRef, lastLoggedArrowsRef,
  };
}
